import React, { useEffect, useRef } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    LayoutDashboard, Network, GitMerge, Activity,
    Radar, Building2, Send, ListTree,
    Search, Bookmark, MessageCircle, Workflow,
    Mic, Shield, FileText, Cpu, Plug, Palette, Scale,
    LogOut, Globe, Sparkles, UsersRound, MessageSquarePlus, SlidersHorizontal,
    Brain, Users, Home, ArrowLeftRight, Server, Briefcase, Handshake,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { setLocale } from "@/lib/i18n";
import { api } from "@/lib/api";
import FeedbackButton from "@/components/feedback/FeedbackButton";
import ChatWidget from "@/components/chat/ChatWidget";
import { OrchestratorTile } from "@/components/brand/OrchestratorLogo";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import SetupProgressBadge from "@/components/dashboard/SetupProgressBadge";
import ThemeToggle from "@/components/ThemeToggle";
import HeaderControls from "@/components/HeaderControls";
import { NotificationDock } from "@/components/NotificationBell";

export default function DashboardLayout() {
    const { t, i18n } = useTranslation();
    const { user, workspace, logout, refreshWorkspace } = useAuth();
    const nav = useNavigate();
    const { pathname } = useLocation();
    const localeSynced = useRef(false);

    // Route-wissel → altijd bovenaan de nieuwe pagina openen (voorkomt dat de
    // scroll-positie van de vorige pagina blijft "hangen" bij tab-navigatie).
    useEffect(() => { window.scrollTo({ top: 0, left: 0 }); }, [pathname]);

    useEffect(() => {
        const accent = workspace?.branding?.accent_color;
        if (accent) document.documentElement.style.setProperty("--jr-brand", accent);
        if (!localeSynced.current && workspace?.locale && workspace.locale !== i18n.language) {
            i18n.changeLanguage(workspace.locale); setLocale(workspace.locale);
        }
        if (workspace?.locale) localeSynced.current = true;
    }, [workspace, i18n]);

    const doLogout = () => { logout(); nav("/"); };
    const switchLang = async (lng) => {
        setLocale(lng); i18n.changeLanguage(lng);
        try { await api.patch("/branding", { locale: lng }); refreshWorkspace(); } catch (e) { /* non-blocking */ }
    };

    const logoUrl = workspace?.branding?.logo_url;
    const faviconUrl = workspace?.branding?.favicon_url;
    const wlName = workspace?.branding?.white_label ? (workspace?.branding?.brand_name || "").trim() : "";
    const accentName = wlName || workspace?.branding?.sender_name?.split(" ")[0] || "Orchestrator";
    useEffect(() => {
        document.title = wlName
            ? `${wlName} — Agentic Sales & Recruitment CRM`
            : "Orchestrator — Agentic Sales & Recruitment CRM";
    }, [wlName]);

    // Apply the workspace favicon to the browser tab (white-label). Restores
    // the default icon when no custom favicon is set.
    useEffect(() => {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
        }
        if (!link.dataset.jrDefault) link.dataset.jrDefault = link.getAttribute("href") || "/favicon.ico";
        link.href = faviconUrl || link.dataset.jrDefault;
    }, [faviconUrl]);

    // --- Nav groups ---
    const STAFFING_NAV = [
        { to: "/app/staffing/candidates", label: "Kandidaten", icon: Users, testid: "nav-staffing-candidates" },
        { to: "/app/staffing/jobs", label: "Jobs", icon: Briefcase, testid: "nav-staffing-jobs" },
        { to: "/app/staffing/pipeline", label: "Pipeline", icon: Workflow, testid: "nav-staffing-pipeline" },
        { to: "/app/staffing/deals", label: "Deals", icon: Handshake, testid: "nav-staffing-deals" },
        { to: "/app/staffing/outreach", label: "Outreach", icon: Send, testid: "nav-staffing-outreach" },
        { to: "/app/voice", label: t("nav.voice"), icon: Mic, testid: "nav-voice" },
    ];
    const SALES_NAV = [
        { to: "/app/sales/signals", label: t("nav.signal_hunting"), icon: Radar, testid: "nav-sales-signals" },
        { to: "/app/sales/job-changes", label: t("nav.job_changes"), icon: ArrowLeftRight, testid: "nav-sales-job-changes" },
        { to: "/app/sales/companies", label: t("nav.companies"), icon: Building2, testid: "nav-sales-companies" },
        { to: "/app/sales/contacts", label: t("nav.contacts"), icon: Users, testid: "nav-sales-contacts" },
        { to: "/app/sales/pipeline", label: t("nav.sales_pipeline"), icon: ListTree, testid: "nav-sales-pipeline" },
        { to: "/app/sales/outreach", label: t("nav.sales_outreach"), icon: Send, testid: "nav-sales-outreach" },
    ];
    const RECRUIT_NAV = [
        { to: "/app/recruit/search", label: t("nav.talent_search"), icon: Search, testid: "nav-recruit-search" },
        { to: "/app/recruit/pool", label: t("nav.talent_pool"), icon: Users, testid: "nav-recruit-pool" },
        { to: "/app/recruit/shortlists", label: t("nav.shortlists"), icon: Bookmark, testid: "nav-recruit-shortlists" },
        { to: "/app/recruit/outreach", label: t("nav.candidate_outreach"), icon: MessageCircle, testid: "nav-recruit-outreach" },
        { to: "/app/recruit/pipeline", label: t("nav.candidate_pipeline"), icon: Workflow, testid: "nav-recruit-pipeline" },
    ];
    const BRIDGE_NAV = [
        { to: "/app/matches", label: t("nav.matches"), icon: GitMerge, testid: "nav-matches" },
        { to: "/app/network", label: "Netwerk", icon: UsersRound, testid: "nav-network" },
        { to: "/app/agents", label: t("nav.agents"), icon: Network, testid: "nav-agents" },
    ];
    const SETTINGS_NAV = [
        { to: "/app/tone-of-voice", label: t("nav.tone_of_voice"), icon: Sparkles, testid: "nav-tone" },
        { to: "/app/agent-memory", label: "Agent Memory", icon: Brain, testid: "nav-agent-memory" },
        { to: "/app/team", label: t("nav.team"), icon: UsersRound, testid: "nav-team" },
        { to: "/app/feedback/mine", label: "Mijn feedback", icon: MessageCircle, testid: "nav-my-feedback" },
        { to: "/app/feedback", label: t("nav.feedback"), icon: MessageSquarePlus, testid: "nav-feedback" },
        { to: "/app/workspace", label: "Workspace", icon: SlidersHorizontal, testid: "nav-workspace" },
        { to: "/app/integrations", label: t("nav.integrations"), icon: Plug, testid: "nav-integrations" },
        { to: "/app/branding", label: t("nav.branding"), icon: Palette, testid: "nav-branding" },
        { to: "/app/bias", label: t("nav.bias"), icon: Scale, testid: "nav-bias" },
        { to: "/app/compliance", label: t("nav.compliance"), icon: Shield, testid: "nav-compliance" },
        { to: "/app/audit", label: t("nav.audit"), icon: FileText, testid: "nav-audit" },
        ...(user?.platform_admin ? [{ to: "/app/platform-admin", label: t("nav.platform_admin"), icon: Server, testid: "nav-platform-admin" }] : []),
    ];

    return (
        <div className="min-h-screen flex bg-[var(--jr-bg)]">
            <aside className="w-64 border-r border-white/8 flex flex-col bg-[var(--jr-bg)] sticky top-0 h-screen shrink-0" data-testid="dashboard-sidebar">
                <div className="px-5 h-16 flex items-center gap-2 border-b border-white/8">
                    {logoUrl ? (
                        <img src={logoUrl} alt="logo" className="w-7 h-7 rounded-md object-cover border border-white/10"/>
                    ) : (
                        <OrchestratorTile size={28} />
                    )}
                    <span className="font-display text-lg tracking-tight truncate">{accentName}</span>
                </div>

                <div className="px-3 py-4 flex-1 overflow-y-auto">
                    <div className="px-2 mb-3">
                        <WorkspaceSwitcher />
                        <div className="mt-1.5 text-[10px] text-[var(--jr-text-2)] font-mono uppercase tracking-[0.16em] px-1">{workspace?.tier} · {workspace?.credits} {t("nav.credits")}</div>
                        <SetupProgressBadge />
                    </div>

                    {/* Top-level */}
                    <nav className="space-y-0.5">
                        <NavItem item={{ to: "/app/home", label: "Home", icon: Home, testid: "nav-home" }}/>
                        <NavItem item={{ to: "/app/dashboard", label: t("nav.dashboard"), icon: LayoutDashboard, testid: "nav-dashboard" }}/>
                        <NavItem item={{ to: "/app/briefing", label: t("nav.briefing"), icon: Sparkles, testid: "nav-briefing" }}/>
                    </nav>

                    <GroupLabel color="var(--jr-amber)" testid="group-staffing">Staffing</GroupLabel>
                    <nav className="space-y-0.5" data-testid="staffing-nav">
                        {STAFFING_NAV.map((n, i) => <NavItem key={n.to} item={n} step={i + 1} pillarColor="var(--jr-amber)"/>)}
                    </nav>

                    <GroupLabel color="var(--jr-brand)" testid="group-sales">{t("nav.sales_pillar")}</GroupLabel>
                    <nav className="space-y-0.5" data-testid="sales-nav">
                        {SALES_NAV.map((n, i) => <NavItem key={n.to} item={n} step={i + 1} pillarColor="var(--jr-brand)"/>)}
                    </nav>

                    <GroupLabel color="var(--jr-amber)" testid="group-recruit">{t("nav.recruitment_pillar")}</GroupLabel>
                    <nav className="space-y-0.5" data-testid="recruit-nav">
                        {RECRUIT_NAV.map((n, i) => <NavItem key={n.to} item={n} step={i + 1} pillarColor="var(--jr-amber)"/>)}
                    </nav>

                    <GroupLabel color="var(--jr-info)" testid="group-bridge">{t("nav.bridge_pillar")}</GroupLabel>
                    <nav className="space-y-0.5">
                        {BRIDGE_NAV.map((n) => <NavItem key={n.to} item={n}/>)}
                    </nav>

                    <GroupLabel testid="group-settings">{t("nav.settings_pillar")}</GroupLabel>
                    <nav className="space-y-0.5">
                        {SETTINGS_NAV.map((n) => <NavItem key={n.to} item={n}/>)}
                    </nav>
                </div>

                <div className="px-3 py-4 border-t border-white/8 space-y-3">
                    <div className="px-2 min-w-0">
                        <div className="text-sm text-white truncate">{user?.name}</div>
                        <div className="text-xs text-[var(--jr-text-3)] truncate">{user?.email}</div>
                    </div>
                    <button onClick={doLogout} data-testid="logout-btn" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[var(--jr-text-2)] hover:text-white hover:bg-white/5 transition-colors">
                        <LogOut size={14}/> {t("nav.logout")}
                    </button>
                </div>
            </aside>

            <main className="flex-1 overflow-x-hidden">
                <Outlet />
            </main>
            {/* Top-right fixed control cluster — language + theme + notification bell */}
            <div className="fixed top-3 right-4 z-40 flex items-center gap-2">
                <HeaderControls variant="app" refreshWorkspace={refreshWorkspace} />
                <NotificationDock inline />
            </div>
            <FeedbackButton />
            <ChatWidget />
        </div>
    );
}

function GroupLabel({ children, color, testid }) {
    return (
        <div className="px-2 mt-5 mb-1.5 flex items-center gap-2" data-testid={testid}>
            {color && <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }}/>}
            <span className="text-[10px] font-mono uppercase tracking-[0.24em] text-[var(--jr-text-3)]">{children}</span>
        </div>
    );
}

function NavItem({ item, step, pillarColor }) {
    const Icon = item.icon;
    const brand = pillarColor || "var(--jr-brand)";
    return (
        <NavLink
            to={item.to}
            data-testid={item.testid}
            end
            className={({ isActive }) =>
                `relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors group ${
                    isActive ? "jr-nav-active" : "jr-nav-idle"
                }`
            }
            style={({ isActive }) => (isActive
                ? { background: `color-mix(in oklab, ${brand} 18%, transparent)`, color: "var(--jr-text)" }
                : { color: "var(--jr-text-2)" })}
        >
            {({ isActive }) => (
                <>
                    <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r transition-colors"
                        style={{ background: isActive ? brand : "transparent" }}/>
                    <span className="pl-1 flex items-center gap-3 flex-1 min-w-0">
                        {step ? (
                            <span className="w-5 h-5 rounded grid place-items-center text-[10px] font-mono shrink-0 font-semibold"
                                style={{
                                    background: isActive ? brand : "var(--jr-white-06)",
                                    color: isActive ? "var(--jr-text-on-brand)" : "var(--jr-text-2)",
                                }}>
                                {step}
                            </span>
                        ) : (
                            <Icon size={14} style={{ color: isActive ? brand : "var(--jr-text-2)" }} />
                        )}
                        <span className="truncate" style={{ fontWeight: isActive ? 600 : 500 }}>{item.label}</span>
                    </span>
                </>
            )}
        </NavLink>
    );
}
